import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { pluralize } from "@prismicio/editor-support/String"
import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  IconButton,
  ListItem,
  Text,
  Tooltip,
  UnorderedList,
  useAlert,
} from "@prismicio/editor-ui"
import { useMemo, useState } from "react"
import { z } from "zod"

import { type EditorConfig, useEditorConfig } from "../../../../../EditorConfig"
import { useSelectionManager } from "../../../../SelectionManager"
import type { MediaAsset } from "../../../hooks/mediaLibraryData"
import { useRevalidateUploaders } from "../../../hooks/uploadersData"
import { useSelectedMedia } from "../../../hooks/useSelectedMedia"
import { useMediaLibrary } from "../../MediaLibraryContext"

export function SidepaneHeader() {
  const [alertOpen, setAlertOpen] = useState(false)

  const selectedMedia = useSelectedMedia()
  const selectionManager = useSelectionManager()

  const isUploading = useMemo(
    () => selectedMedia.some(({ id }) => id.startsWith("temp")),
    [selectedMedia],
  )

  const [firstMedia] = selectedMedia
  if (!firstMedia) return null

  if (selectionManager.selectedCount > 1) {
    return (
      <Box gap={8} alignItems="center">
        <Text
          variant="emphasized"
          noWrap
          sx={{
            flexGrow: 1,
          }}
        >
          {selectionManager.selectedCount} media selected
        </Text>

        <Tooltip content="Open all selected media in new tabs">
          <IconButton
            icon="openInFull"
            variant="solid"
            size="small"
            onClick={() =>
              // this will open all media in new tabs in some browsers
              // if not the user will have to explicitly allow it
              // https://developer.mozilla.org/en-US/docs/Web/API/Window/open#usage_notes
              selectedMedia.forEach((media) => window.open(media.url, "_blank"))
            }
          />
        </Tooltip>
        <Tooltip content="Remove selected media">
          {/* TODO: add outline variant to Button: PBD-1256 */}
          <IconButton
            icon="delete"
            variant="solid"
            color="tomato"
            size="small"
            hiddenLabel="Remove media"
            onClick={() => setAlertOpen(true)}
            disabled={isUploading}
          />
        </Tooltip>

        <RemoveMediaConfirmationDialog
          open={alertOpen}
          onOpenChange={setAlertOpen}
          media={selectedMedia}
        />
      </Box>
    )
  }

  return (
    <Box gap={8} alignItems="center">
      <Text variant="emphasized" noWrap sx={{ flexGrow: 1 }}>
        {firstMedia.filename}
      </Text>

      <Box gap={8}>
        <Tooltip content="Open media in new tab">
          <IconButton
            icon="openInFull"
            variant="solid"
            size="small"
            hiddenLabel="Open media in new tab"
            onClick={() => window.open(firstMedia.url, "_blank")}
            disabled={isUploading}
          />
        </Tooltip>

        <Tooltip content="Copy media URL">
          <IconButton
            icon="link"
            variant="solid"
            size="small"
            hiddenLabel="Copy media URL"
            onClick={() => navigator.clipboard.writeText(firstMedia.url)}
            disabled={isUploading}
          />
        </Tooltip>

        <Tooltip content="Remove media">
          {/* TODO: add outline variant to Button: PBD-1256 */}
          <IconButton
            icon="delete"
            variant="solid"
            color="tomato"
            size="small"
            hiddenLabel="Remove media"
            onClick={() => setAlertOpen(true)}
            disabled={isUploading}
          />
        </Tooltip>

        <RemoveMediaConfirmationDialog
          open={alertOpen}
          onOpenChange={setAlertOpen}
          media={firstMedia}
        />
      </Box>
    </Box>
  )
}

interface RemoveMediaConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  media: MediaAsset | MediaAsset[]
}

function RemoveMediaConfirmationDialog(
  props: RemoveMediaConfirmationDialogProps,
) {
  const { open, onOpenChange, media } = props

  const config = useEditorConfig()
  const { showAlert } = useAlert()
  const { deleteMedia } = useMediaLibrary()
  const selectionManager = useSelectionManager()
  const revalidateUploaders = useRevalidateUploaders()

  const [loading, setLoading] = useState(false)

  const hasMultipleMedia = Array.isArray(media)
  const itemLabel = hasMultipleMedia ? "these items" : "this item"

  function onRemoveMedia() {
    setLoading(true)

    const selectionState = selectionManager.getSelectionState()
    // TODO: [PBD-1029] adapt logic to handle select all, and select exclusions
    const ids = selectionState.selectedIds

    removeMedia({ ids, config })
      .then(() => {
        onOpenChange(false)
        deleteMedia(ids)
        selectionManager.clearSelection()
        revalidateUploaders()
      })
      .catch(() => {
        onOpenChange(false)
        showAlert({
          title: "Error",
          subtitle: `Something went wrong when removing ${itemLabel}.`,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader
        icon="alert"
        title={`Remove ${itemLabel} from your media library?`}
      />
      <DialogDescription asChild>
        <DialogContent>
          <Box padding={16} flexDirection="column">
            <Text>
              You are about to delete {itemLabel}: &nbsp;
              {!hasMultipleMedia && <b>{media.filename}</b>}
            </Text>
            {hasMultipleMedia && <DeletionInfoList media={media} />}
          </Box>
        </DialogContent>
      </DialogDescription>
      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton
          onClick={onRemoveMedia}
          color="tomato"
          loading={loading}
        >
          Yes, I want to remove {itemLabel}
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

const displayItemsLimit = 15

interface DeletionInfoListProps {
  media: MediaAsset[]
}

function DeletionInfoList(props: DeletionInfoListProps) {
  const { media } = props

  const mediaDisplayedItems = media.slice(0, displayItemsLimit)

  const mediaItemsAreExceedingLimit = media.length > displayItemsLimit
  const mediaItemsExceedingLimit = mediaItemsAreExceedingLimit
    ? media.length - displayItemsLimit
    : 0
  const itemLabel = pluralize(mediaItemsExceedingLimit, "item")

  return (
    <UnorderedList density="compact" sx={{ marginTop: 8 }}>
      {mediaDisplayedItems.map((item, index) => (
        <ListItem key={index}>
          <b>{item.filename}</b>
        </ListItem>
      ))}
      {mediaItemsAreExceedingLimit && (
        <ListItem>
          And <b>{mediaItemsExceedingLimit}</b> more {itemLabel}...
        </ListItem>
      )}
    </UnorderedList>
  )
}

function removeMedia(params: { ids: string[]; config: EditorConfig }) {
  const { ids, config } = params

  if (!config.mediaLibrary) throw new Error("Media Library not configured.")
  if (ids.length === 0) throw new Error("No ids provided during media deletion")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  if (ids.length === 1) {
    const [id] = ids as [string]

    return request(new URL(`assets/${id}`, assetsApiBaseUrl), {
      headers: { repository },
      method: "DELETE",
      credentials: getRequestCredentials(authStrategy),
      okType: z.unknown(),
    })
  }

  return request(new URL(`assets/bulk-delete`, assetsApiBaseUrl), {
    headers: { repository },
    method: "POST",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
    body: {
      ids,
    },
  })
}
